import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SolutionsSection = () => {
  const solutions = [
  {
    title: "Provider Groups & Specialty Medicine",
    description: "Track coverage policies that impact your practice. Monitor Medicare, Medicaid, and commercial payer changes for your specialty with customized alerts and analysis.",
    mockup: (
      <div className="bg-gradient-to-br from-[#1B1B20] to-[#2A2A30] rounded-xl p-6 text-white h-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-bold mb-1">Specialty Coverage Monitor</h4>
            <p className="text-xs text-gray-400">Cardiology - Medicare Part B</p>
          </div>
          <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1.5 rounded-full">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-green-400">Live</span>
          </div>
        </div>
        <div className="space-y-3">
          {[
            { code: 'CPT 93000', status: 'Coverage Updated', impact: 'High', color: '#ED1C24' },
            { code: 'CPT 93015', status: 'Reimbursement Change', impact: 'Medium', color: '#F59E0B' },
            { code: 'CPT 93306', status: 'Prior Auth Required', impact: 'High', color: '#ED1C24' }
          ]?.map((item, idx) => (
            <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10 hover:border-[#1A73E8]/50 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item?.color }}></div>
                  <div>
                    <p className="text-sm font-semibold">{item?.code}</p>
                    <p className="text-xs text-gray-400">{item?.status}</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 bg-white/10 rounded-full">{item?.impact}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xl font-bold text-[#1A73E8]">847</p>
            <p className="text-xs text-gray-400">Tracked</p>
          </div>
          <div>
            <p className="text-xl font-bold text-[#10B981]">23</p>
            <p className="text-xs text-gray-400">Updates</p>
          </div>
          <div>
            <p className="text-xl font-bold text-[#F59E0B]">12</p>
            <p className="text-xs text-gray-400">Alerts</p>
          </div>
        </div>
      </div>
    ),
    icon: "Stethoscope",
    features: ["Specialty-Specific Monitoring", "Coverage Change Alerts", "Reimbursement Tracking", "Multi-Payer Analysis"],
    link: "/platform-demo"
  },
  {
    title: "Device Manufacturers",
    description: "Stay ahead of coverage policy changes that affect your products. Monitor device-specific policies across all payers and identify market access opportunities.",
    mockup: (
      <div className="bg-white rounded-xl p-6 border border-gray-200 h-full">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-[#1B1B20]">Device Coverage Analysis</h4>
          <Icon name="Package" size={20} color="#1A73E8" />
        </div>
        <div className="space-y-3">
          {[
            { device: 'Cardiac Stent - Model X200', payers: 'UHC, Aetna, BCBS', coverage: '94%', trend: 'up' },
            { device: 'Hip Implant - Series 500', payers: 'Medicare, Cigna', coverage: '87%', trend: 'up' },
            { device: 'Spinal Device - Pro Line', payers: 'Humana, Anthem', coverage: '76%', trend: 'down' }
          ]?.map((item, idx) => (
            <div key={idx} className="bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-lg p-4 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-semibold text-[#1B1B20] flex-1">{item?.device}</p>
                <div className="flex items-center gap-1">
                  <Icon name={item?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={14} color={item?.trend === 'up' ? '#10B981' : '#ED1C24'} />
                  <span className={`text-sm font-bold ${item?.trend === 'up' ? 'text-[#10B981]' : 'text-[#ED1C24]'}`}>{item?.coverage}</span>
                </div>
              </div>
              <p className="text-xs text-gray-600">{item?.payers}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Market Access Score</span>
            <span className="font-bold text-[#1A73E8]">8.7/10</span>
          </div>
        </div>
      </div>
    ),
    icon: "Package",
    features: ["Device Policy Tracking", "Market Access Intelligence", "Coverage Analysis", "Competitive Insights"],
    link: "/enterprise-solutions"
  },
  {
    title: "API Integration",
    description: "Seamlessly integrate PolicyAlerts into your existing systems with our enterprise-grade API. Access real-time policy data programmatically.",
    mockup: (
      <div className="bg-[#1B1B20] rounded-xl p-6 text-white h-full font-mono">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#ED1C24]"></div>
            <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
            <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
          </div>
          <span className="text-xs text-gray-400">api.policyalerts.com</span>
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex gap-2">
            <span className="text-[#ED1C24]">POST</span>
            <span className="text-gray-400">/api/v1/policies/search</span>
          </div>
          <div className="bg-white/5 rounded p-3 border border-white/10">
            <div className="text-[#10B981]">{'{'}</div>
            <div className="ml-4 text-[#1A73E8]">"payer": <span className="text-[#F59E0B]">"Medicare"</span>,</div>
            <div className="ml-4 text-[#1A73E8]">"specialty": <span className="text-[#F59E0B]">"Cardiology"</span>,</div>
            <div className="ml-4 text-[#1A73E8]">"status": <span className="text-[#F59E0B]">"active"</span></div>
            <div className="text-[#10B981]">{'}'}</div>
          </div>
          <div className="flex items-center gap-2 text-[#10B981] mt-3">
            <Icon name="Check" size={14} />
            <span>200 OK - 847 policies returned</span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-bold text-[#1A73E8]">99.9%</p>
            <p className="text-xs text-gray-400">Uptime</p>
          </div>
          <div>
            <p className="text-lg font-bold text-[#10B981]">&lt; 50ms</p>
            <p className="text-xs text-gray-400">Response</p>
          </div>
          <div>
            <p className="text-lg font-bold text-[#F59E0B]">RESTful</p>
            <p className="text-xs text-gray-400">API</p>
          </div>
        </div>
      </div>
    ),
    icon: "Code",
    features: ["RESTful API", "Webhooks", "Custom Integration", "Developer Support"],
    link: "/api-documentation"
  }];


  return (
    <section className="bg-gradient-to-b from-white to-gray-50 py-16 md:py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#ED1C24]/10 rounded-full mb-4 md:mb-6">
            <Icon name="Layers" size={16} color="#ED1C24" />
            <span className="text-xs md:text-sm font-medium text-[#ED1C24]">Solutions</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B1B20] mb-4 md:mb-6">
            Built for Healthcare Organizations
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            Whether you're a provider group, specialty practice, or device manufacturer—PolicyAlerts delivers the policy intelligence you need.
          </p>
        </div>

        <div className="space-y-12 md:space-y-16 lg:space-y-20">
          {solutions?.map((solution, idx) =>
          <div
            key={idx}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-16 items-center ${
            idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`
            }>

              <div className={`${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  {solution?.mockup}
                </div>
              </div>

              <div className={`${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#1A73E8]/10 rounded-full mb-4 md:mb-6">
                  <Icon name={solution?.icon} size={20} color="#1A73E8" />
                  <span className="text-xs md:text-sm font-medium text-[#1A73E8]">Solution</span>
                </div>

                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B1B20] mb-4 md:mb-6">
                  {solution?.title}
                </h3>
                <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                  {solution?.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
                  {solution?.features?.map((feature, fIdx) =>
                <div key={fIdx} className="flex items-center gap-2 md:gap-3">
                      <div className="w-5 h-5 md:w-6 md:h-6 bg-[#10B981]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={14} color="#10B981" />
                      </div>
                      <span className="text-sm md:text-base text-gray-700">{feature}</span>
                    </div>
                )}
                </div>

                <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                className="bg-[#1A73E8] hover:bg-[#1A73E8]/90"
                asChild>

                  <Link to={solution?.link}>Learn More</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default SolutionsSection;